// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  FULFILLMENT_STATUS_COLORS,
  FulfillmentStatus,
} from '@/constants/fulfillment-status';
import {
  Checkbox,
  Chip,
  FormControlLabel,
  MenuItem,
  styled,
} from '@mui/material';

const FilterCheckbox = styled(Checkbox)(({ theme }) => ({
  paddingTop: 0,
  paddingBottom: 0,
  '&.MuiCheckbox-indeterminate': {
    color: theme.palette.primary.light,
  },
}));

interface Props {
  /**
   * The fulfillment status for this item.
   */
  status: FulfillmentStatus;

  /**
   * The label to display in the chip.
   */
  label: string;

  /**
   * If true, the checkbox is checked.
   */
  isActive: boolean;

  /**
   * A callback fired when the checkbox is toggled.
   */
  onChange: (isActive: boolean) => void;
}

/**
 * Gets the chip configuration for a fulfillment status
 */
const getChipConfig = (status: FulfillmentStatus) => {
  return FULFILLMENT_STATUS_COLORS[status];
};

/**
 * A menu item with a checkbox and a colored chip for fulfillment status.
 */
export function FulfillmentFilterMenuItem(props: Props) {
  const chipConfig = getChipConfig(props.status);

  const handleChange = (_: unknown, isChecked: boolean) => {
    props.onChange(isChecked);
  };

  return (
    <MenuItem
      dense
      disableGutters
      sx={{ marginLeft: 1, marginRight: 1, borderRadius: 1 }}
    >
      <FormControlLabel
        checked={props.isActive}
        onChange={handleChange}
        sx={{ paddingX: 1 }}
        label={
          chipConfig.customColor ? (
            <Chip
              label={props.label}
              variant="outlined"
              size="small"
              sx={{
                borderColor: chipConfig.customColor,
                color: chipConfig.customColor,
              }}
            />
          ) : (
            <Chip
              label={props.label}
              variant="outlined"
              color={chipConfig.color}
              size="small"
            />
          )
        }
        control={<FilterCheckbox disableRipple size="small" />}
      />
    </MenuItem>
  );
}
